import { useSelector, useDispatch } from "react-redux";
import React, { useCallback } from "react";
import { Provider, connect } from "react-redux";
import { createReduxStore } from "@/store/reduxHelpers";
import { reducers } from "@/modules/pg/shared/planningTable/reducers/reducers";

import RecommendPlan from "../toolbar/RecommendPlan";
import PlanningTable from "./PlanningTable";
import Recalculate from "../toolbar/Recalculate";
import { ExportBaselineParallelReportPrefetchSubscriber } from "../lib/exportBaselineParallelReport";
import { subscribePlanningTableLoadFullUiWallTime } from "@/modules/pg/shared/planningTable/lib/planningTableLoadWallTimeSession";
import Filters from "./Filters";
import SaveButtonsContainer from "../toolbar/SaveButtonsContainer";
import { PLANNING_TABLE_DISPLAY_TYPES } from "@/modules/pg/shared/planningTable/toolbar/DisplayTypesMenu";
import { RESET_FILTERS } from "@/modules/pg/shared/planningTable/reducers/filters/filters";
import { DraggableConsumer, DraggableProvider } from "@/modules/pg/shared/planningTable/draggable/DraggableContext";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { AreYouSureModal } from "@/modules/shared/ui/AreYouSureModal";
import { Dropdown } from "react-bootstrap";
import { SET_ALL_AS_UNSCHEDULED } from "@/modules/pg/shared/planningTable/reducers/common";
import {
    CLEAR_MULTISELECT_ORDERS,
    SET_SHOW_STOCK_REQUIREMENTS,
    SHOW_UNSCHEDULED_ORDERS,
} from "@/modules/pg/shared/planningTable/reducers/planningTable";
import { TOGGLE_WATERFALL_MODE } from "@/modules/pg/shared/planningTable/reducers/gantt/properties";
import { REPORT_LOADING_ACTION_NAME } from "@/modules/pg/shared/planningTable/reducers/report";
import MouseCursor from "@/modules/pg/shared/planningTable/interaction/MouseCursor";
import { DraggableLogic, ReduxFilters } from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";
import { ConnectedViewMenuAlerts } from "../alerts/AlertsModal";
import RetryButton from "../toolbar/RetryButton";
import { PerformanceAnalysisButton } from "@/modules/shared/ui/performance/PlanningTablePerformance";
import BadgeNumberAlerts from "../alerts/BadgeNumberAlerts";
import AbortButton from "../toolbar/AbortButton";
import OperationLinksModal from "../modals/OperationLinksModal";
import LeavePageWarning from "@/modules/shared/ui/LeavePageWarning";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import local_settings, {
    FIELD_SHOW_PLANNING_DETAILS_FILTER,
    FIELD_PLANNING_DETAILS_POSITION,
} from "@/lib/localSettings";

import type { History, Location } from "history";
import type { Dispatch, Store, Unsubscribe } from "redux";
import type { ReportLoadingAction } from "@/modules/pg/shared/planningTable/reducers/report";
import type { ResetFiltersAction } from "@/modules/pg/shared/planningTable/reducers/filters/stockRequirementsFilters";
import type { ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";
import type {
    SetShowStockRequirements,
    ShowUnscheduledOrdersAction,
    SetPlanningDetailsSelectedView,
    ClearMultiselectOrdersAction,
} from "@/modules/pg/shared/planningTable/reducers/planningTable";
import type { DraggableProviderContext } from "@/modules/pg/shared/planningTable/draggable/DraggableContext";
import type { PlanningDetailsPosition } from "@/modules/pg/shared/viewsCommon";

import img_eye_solid from "@/img/eye-solid.svg";
import img_pen_ruler_solid from "@/img/pen-ruler-solid.svg";
import img_square_left_half from "@/img/square_left_half.svg";
import img_square_bottom_half from "@/img/square_bottom_half.svg";

import "./PlanningTableApp.scss";

const ClearMultiselect = () => {
    const dispatch = useDispatch<Dispatch<ClearMultiselectOrdersAction>>();
    const multiselect_count = useSelector((state: ReduxState) => {
        const { multiselect_order_uuids } = state.gantt_chart_planning_table;
        return multiselect_order_uuids === null ? 0 : multiselect_order_uuids.length;
    });

    const handleClearMultiselectClick = useCallback(() => {
        dispatch({
            type: CLEAR_MULTISELECT_ORDERS,
            data: undefined,
        });
    }, [dispatch]);

    if (multiselect_count === 0) {
        return null;
    }

    return (
        <div className="ml-2 d-flex align-items-center">
            <button className="btn btn-short btn-outline-secondary" onClick={handleClearMultiselectClick}>
                <i className="far fa-trash-alt"></i>{" "}
                <span>{translate("common.clear_selection", "Clear selection")}</span>{" "}
                <span className="badge badge-danger">{multiselect_count}</span>
            </button>
        </div>
    );
};

// StockRequirementsToggle

type StockRequirementsToggleOwnProps = {};
type StockRequirementsToggleActionTypes =
    | SetShowStockRequirements
    | ShowUnscheduledOrdersAction
    | ResetFiltersAction
    | ReportLoadingAction
    | SetPlanningDetailsSelectedView;

type StockRequirementsToggleProps = StockRequirementsToggleOwnProps & StockRequirementsToggleReduxProps;

class StockRequirementsToggle extends React.Component<StockRequirementsToggleProps> {
    onToggle = () => {
        this.props.reduxDispatch({
            type: REPORT_LOADING_ACTION_NAME,
            data: true,
        });
        setTimeout(() => {
            this.props.reduxDispatch({
                type: SET_SHOW_STOCK_REQUIREMENTS,
                data: !this.props.show_stock_requirements,
            });
            this.props.reduxDispatch({ type: RESET_FILTERS, data: undefined });
            this.props.reduxDispatch({ type: REPORT_LOADING_ACTION_NAME, data: false });
        }, 10);
    };

    render() {
        const menu_text = this.props.show_stock_requirements
            ? translate("common.hide_stock_requirements", "Hide stock requirements")
            : translate("common.show_stock_requirements", "Show stock requirements");

        return (
            <button className="dropdown-item" onClick={this.onToggle}>
                {menu_text}
            </button>
        );
    }
}

const stockRequirementsToggleMapState = (state: ReduxState) => {
    const planning_table = state.gantt_chart_planning_table;
    return {
        show_stock_requirements: planning_table.show_stock_requirements,
    };
};

const stockRequirementsToggleMapDispatch = (dispatch: Dispatch<StockRequirementsToggleActionTypes>) => ({
    reduxDispatch: dispatch,
});

type StockRequirementsToggleReduxProps = ReturnType<typeof stockRequirementsToggleMapState> &
    ReturnType<typeof stockRequirementsToggleMapDispatch>;

const ConnectedStockRequirementsToggle = connect<
    ReturnType<typeof stockRequirementsToggleMapState>,
    ReturnType<typeof stockRequirementsToggleMapDispatch>,
    StockRequirementsToggleOwnProps,
    ReduxState
>(
    stockRequirementsToggleMapState,
    stockRequirementsToggleMapDispatch
)(StockRequirementsToggle);

// UnscheduledOrdersToggle

type UnscheduledOrdersToggleOwnProps = {};
type UnscheduledOrdersToggleActionTypes = ReportLoadingAction | ShowUnscheduledOrdersAction;

type UnscheduledOrdersToggleProps = UnscheduledOrdersToggleOwnProps & UnscheduledOrdersToggleReduxProps;

class UnscheduledOrdersToggle extends React.Component<UnscheduledOrdersToggleProps> {
    onToggle = () => {
        this.props.reduxDispatch({
            type: SHOW_UNSCHEDULED_ORDERS,
            data: !this.props.show_unscheduled_orders,
        });
    };

    render() {
        const menu_text = this.props.show_unscheduled_orders
            ? translate("common.hide_unscheduled_orders", "Hide unscheduled orders")
            : translate("common.show_unscheduled_orders", "Show unscheduled orders");

        return (
            <button className="dropdown-item" onClick={this.onToggle}>
                {menu_text}
            </button>
        );
    }
}

const unscheduledOrdersToggleMapState = (state: ReduxState) => {
    const planning_table = state.gantt_chart_planning_table;
    return {
        show_unscheduled_orders: planning_table.show_unscheduled_orders,
    };
};

const unscheduledOrdersToggleMapDispatch = (dispatch: Dispatch<UnscheduledOrdersToggleActionTypes>) => ({
    reduxDispatch: dispatch,
});

type UnscheduledOrdersToggleReduxProps = ReturnType<typeof unscheduledOrdersToggleMapState> &
    ReturnType<typeof unscheduledOrdersToggleMapDispatch>;

const ConnectedUnsheduledOrdersToggle = connect<
    ReturnType<typeof unscheduledOrdersToggleMapState>,
    ReturnType<typeof unscheduledOrdersToggleMapDispatch>,
    UnscheduledOrdersToggleOwnProps,
    ReduxState
>(
    unscheduledOrdersToggleMapState,
    unscheduledOrdersToggleMapDispatch
)(UnscheduledOrdersToggle);

type PlanningTableMenuProps = {
    show_import_plan_modal: boolean;
    show_import_plant_plan_modal: boolean;
    show_operation_links_modal: boolean;
    show_planning_details_filter: boolean;
    planning_details_position: PlanningDetailsPosition;
    line_group_uuid: string;
    plant_uuid: string;
    selected_display_type: keyof typeof PLANNING_TABLE_DISPLAY_TYPES;
    onResetFilter: () => void;
    onShowPlanningTableFilter: () => void;
    onSetPlanningTablePosition: (position: PlanningDetailsPosition) => void;
    onShowImportPlanOnDemand: () => void;
    onCloseImportPlantPlanOnDemand: () => void;
    onShowImportPlantPlanOnDemand: () => void;
    onCloseImportPlanOnDemand: () => void;
    onShowOperationLinksModal: () => void;
    onCloseOperationLinksModal: () => void;
    onSetAllAsUnscheduled: () => void;
};
type PlanningTableMenuState = {};

class PlanningTableMenu extends React.Component<PlanningTableMenuProps, PlanningTableMenuState> {
    renderSaveButton = () => {
        return <SaveButtonsContainer plant_uuid={this.props.plant_uuid} line_group_uuid={this.props.line_group_uuid} />;
    };

    renderOperationLinksModalButton = () => {
        return (
            <>
                <button className="dropdown-item" onClick={this.props.onShowOperationLinksModal}>
                    {translate("common.linked_operations_overview", "Linked operations overview")}
                </button>
                <OperationLinksModal
                    show={this.props.show_operation_links_modal}
                    onClose={this.props.onCloseOperationLinksModal}
                />
            </>
        );
    };

    render() {
        return (
            <div className="planning-table-app-menu" style={{ padding: "10px", justifyContent: "space-between" }}>
                <div style={{ display: "flex" }}>
                    <div className="nav-item active" key="div_view" title={translate("common.view", "View")}>
                        <Dropdown>
                            <Dropdown.Toggle
                                as="a"
                                className="nav-link nav-link--neutral"
                                href="#"
                                id="dropdownMenuManufacturing"
                                style={{ cursor: "pointer" }}
                            >
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <span>
                                        <img src={img_eye_solid} />
                                    </span>
                                    <span className="nav_item__text" style={{ marginLeft: "5px" }}>
                                        {translate("common.view", "View")}
                                    </span>
                                    <i className="fas fa-chevron-down"></i>

                                    <BadgeNumberAlerts />
                                </div>
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item as="button" onClick={this.props.onShowPlanningTableFilter}>
                                    {this.props.show_planning_details_filter
                                        ? translate("common.hide_planning_details", "Hide table")
                                        : translate("common.show_planning_details", "Show table")}
                                </Dropdown.Item>
                                <ConnectedStockRequirementsToggle />
                                <ConnectedUnsheduledOrdersToggle />
                                {this.renderOperationLinksModalButton()}
                                <ConnectedViewMenuAlerts />
                                {this.props.show_planning_details_filter && (
                                    <>
                                        <Dropdown.Divider />
                                        <div className="table-position-menu">
                                            <span>{translate("common.table_details_position", "Table position")}</span>
                                            <span>
                                                <button
                                                    className={
                                                        this.props.planning_details_position === "left"
                                                            ? "active"
                                                            : undefined
                                                    }
                                                    onClick={() => this.props.onSetPlanningTablePosition("left")}
                                                >
                                                    <img src={img_square_left_half} />
                                                </button>
                                                <button
                                                    className={
                                                        this.props.planning_details_position === "bottom"
                                                            ? "active"
                                                            : undefined
                                                    }
                                                    onClick={() => this.props.onSetPlanningTablePosition("bottom")}
                                                >
                                                    <img src={img_square_bottom_half} />
                                                </button>
                                            </span>
                                        </div>
                                    </>
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                    <div className="nav-item active" key="div_actions" title={translate("common.actions", "Actions")}>
                        <Dropdown>
                            <Dropdown.Toggle
                                as="a"
                                className="nav-link nav-link--neutral"
                                href="#"
                                id="dropdownMenuManufacturing"
                                style={{ cursor: "pointer" }}
                            >
                                <span>
                                    <img src={img_pen_ruler_solid} />
                                </span>
                                <span className="nav_item__text" style={{ marginLeft: "5px" }}>
                                    {translate("common.actions", "Actions")}
                                </span>
                                <i className="fas fa-chevron-down" />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item as="button" onClick={this.props.onSetAllAsUnscheduled}>
                                    {translate("common.set_all_as_unscheduled", "Set all as unscheduled")}
                                </Dropdown.Item>
                                <RetryButton />
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                    <ClearMultiselect />
                </div>

                <div className="nav-item" style={{ flex: 1, maxWidth: 600 }}>
                    <Filters line_group_uuid={this.props.line_group_uuid} />
                </div>

                <div style={{ display: "flex", gap: 10, float: "right", marginLeft: 10 }}>
                    <div className="d-flex align-items-center ml-2">
                        <PerformanceAnalysisButton />
                    </div>
                    <div>{this.renderSaveButton()}</div>
                    <div>
                        <RecommendPlan
                            plant_uuid={this.props.plant_uuid}
                            line_group_uuid={this.props.line_group_uuid}
                        />
                    </div>
                    <AbortButton />
                </div>
            </div>
        );
    }
}

type PlanningTableProps = {
    plant_uuid: string;
    line_group_uuid: string;
    history: History;
    location: Location;
    reload?: () => void;
};
type PlanningTableState = {
    show_import_plan_modal: boolean;
    show_import_plant_plan_modal: boolean;
    show_operation_links_modal: boolean;
    show_planning_details_filter: boolean;
    planning_details_position: PlanningDetailsPosition;
    show_are_you_sure: boolean;
    selected_display_type: keyof typeof PLANNING_TABLE_DISPLAY_TYPES;
};

export default class PlanningTableApp extends React.Component<PlanningTableProps, PlanningTableState> {
    store: Store<ReduxState>;

    state: PlanningTableState = {
        show_import_plan_modal: false,
        show_import_plant_plan_modal: false,
        show_operation_links_modal: false,
        show_planning_details_filter: local_settings.getItem(FIELD_SHOW_PLANNING_DETAILS_FILTER) !== "false",
        planning_details_position:
            local_settings.getItem(FIELD_PLANNING_DETAILS_POSITION) === "bottom" ? "bottom" : "left",
        show_are_you_sure: false,
        selected_display_type: PLANNING_TABLE_DISPLAY_TYPES.gantt,
    };

    recalculate: Recalculate | null = null;
    exportBaselineParallelPrefetch: ExportBaselineParallelReportPrefetchSubscriber | null = null;
    unsubscribeLoadFullUiWallTime: Unsubscribe | null = null;

    constructor(props: PlanningTableProps) {
        super(props);
        this.store = createReduxStore(reducers);

        this.recalculate = new Recalculate(this.store.dispatch, this.store);
        this.exportBaselineParallelPrefetch = new ExportBaselineParallelReportPrefetchSubscriber(this.store);
        ReduxFilters.planning_table_store = this.store;
        this.unsubscribeLoadFullUiWallTime = subscribePlanningTableLoadFullUiWallTime(this.store);
    }

    componentDidMount() {
        if (document.body) {
            document.body.classList.add("overflow-hidden");
        }

        window.screen.orientation.addEventListener("change", this.onOrienationChange);
    }

    componentWillUnmount() {
        if (this.unsubscribeLoadFullUiWallTime) {
            this.unsubscribeLoadFullUiWallTime();
            this.unsubscribeLoadFullUiWallTime = null;
        }

        if (document.body) {
            document.body.classList.remove("overflow-hidden");
        }

        window.screen.orientation.removeEventListener("change", this.onOrienationChange);
        this.recalculate?.destroy();
        this.recalculate = null;
        this.exportBaselineParallelPrefetch?.destroy();
        this.exportBaselineParallelPrefetch = null;
    }

    onOrienationChange = (event: Event) => {
        const type = (event.target as ScreenOrientation).type;
        const planning_details_position = type === "portrait-primary" ? "bottom" : "left";
        this.setState({ planning_details_position });
    };

    onShowImportPlanOnDemand = () => {
        this.setState({ show_import_plan_modal: true });
    };

    onShowImportPlantPlanOnDemand = () => {
        this.setState({ show_import_plant_plan_modal: true });
    };

    onShowOperationLinksModal = () => {
        this.setState({ show_operation_links_modal: true });
    };

    onCloseImportPlanOnDemand = () => {
        this.setState({ show_import_plan_modal: false });
    };

    onCloseImportPlantPlanOnDemand = () => {
        this.setState({ show_import_plant_plan_modal: false });
    };

    onCloseOperationLinksModal = () => {
        this.setState({ show_operation_links_modal: false });
    };

    onDisplayTypeChanged = (display_type_value: keyof typeof PLANNING_TABLE_DISPLAY_TYPES) => {
        this.setState({ selected_display_type: display_type_value });
    };

    onResetFilter = () => {
        if (this.store) {
            this.store.dispatch({ type: RESET_FILTERS, data: undefined });
            DraggableLogic.changeMouseCursor(null);
        }
    };

    onShowPlanningTableFilter = () => {
        const show_planning_details_filter = !this.state.show_planning_details_filter;
        this.setState({ show_planning_details_filter });
        local_settings.setItem(FIELD_SHOW_PLANNING_DETAILS_FILTER, show_planning_details_filter.toString());
    };

    onSetPlanningTablePosition = (position: PlanningDetailsPosition) => {
        this.setState({ planning_details_position: position });
        local_settings.setItem(FIELD_PLANNING_DETAILS_POSITION, position.toString());
        if (position === "bottom" && this.store) {
            this.store.dispatch({ type: TOGGLE_WATERFALL_MODE, data: false });
        }
    };

    onSetAllAsUnscheduled = () => {
        this.setState({ show_are_you_sure: true });
    };

    onAreYouSureAccept = () => {
        this.setState({ show_are_you_sure: false }, () => {
            const store = this.store;
            if (store) {
                store.dispatch({ type: SET_ALL_AS_UNSCHEDULED, data: undefined });
            }
        });
    };

    onAreYouSureCancel = () => {
        this.setState({ show_are_you_sure: false });
    };

    render() {
        return (
            <Provider store={this.store}>
                <LeavePageWarning history={this.props.history} location={this.props.location}>
                    {translate("common.leave_page_warning", "Are you sure you want to leave this page?")}
                    <ErrorComponent
                        className="mb-0"
                        type="warning"
                        msg={translate(
                            "Manufacturing.Planning.if_you_leave_all_unsaved_changes_will_be_lost",
                            "If you leave planning table all unsaved changes will be lost."
                        )}
                    />
                </LeavePageWarning>
                <AreYouSureModal
                    onAccept={this.onAreYouSureAccept}
                    onCancel={this.onAreYouSureCancel}
                    show={this.state.show_are_you_sure}
                ></AreYouSureModal>
                <PlanningTableMenu
                    plant_uuid={this.props.plant_uuid}
                    line_group_uuid={this.props.line_group_uuid}
                    show_import_plan_modal={this.state.show_import_plan_modal}
                    show_import_plant_plan_modal={this.state.show_import_plant_plan_modal}
                    show_operation_links_modal={this.state.show_operation_links_modal}
                    show_planning_details_filter={this.state.show_planning_details_filter}
                    planning_details_position={this.state.planning_details_position}
                    onResetFilter={this.onResetFilter}
                    onShowPlanningTableFilter={this.onShowPlanningTableFilter}
                    onSetPlanningTablePosition={this.onSetPlanningTablePosition}
                    onCloseImportPlantPlanOnDemand={this.onCloseImportPlantPlanOnDemand}
                    onShowImportPlantPlanOnDemand={this.onShowImportPlantPlanOnDemand}
                    onCloseImportPlanOnDemand={this.onCloseImportPlanOnDemand}
                    onShowImportPlanOnDemand={this.onShowImportPlanOnDemand}
                    onCloseOperationLinksModal={this.onCloseOperationLinksModal}
                    onShowOperationLinksModal={this.onShowOperationLinksModal}
                    onSetAllAsUnscheduled={this.onSetAllAsUnscheduled}
                    selected_display_type={this.state.selected_display_type}
                />
                <DraggableProvider>
                    <DraggableConsumer>
                        {(value?: DraggableProviderContext | null) => {
                            return (
                                <PlanningTable
                                    history={this.props.history}
                                    plant_uuid={this.props.plant_uuid}
                                    line_group_uuid={this.props.line_group_uuid}
                                    show_planning_details_filter={this.state.show_planning_details_filter}
                                    planning_details_position={this.state.planning_details_position}
                                    selected_display_type={this.state.selected_display_type}
                                    draggableDispatch={value != null ? value.dispatch : () => {}}
                                    onDisplayTypeChanged={this.onDisplayTypeChanged}
                                />
                            );
                        }}
                    </DraggableConsumer>
                </DraggableProvider>
                <MouseCursor />
            </Provider>
        );
    }
}
